import type { AnchorHTMLAttributes, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { LinkTarget } from './types'

export interface SmartLinkProps extends LinkTarget {
  children: ReactNode
  className?: string
  /** Forwarded to whichever element is rendered — the current-page marker in the nav. */
  'aria-current'?: AnchorHTMLAttributes<HTMLAnchorElement>['aria-current']
  'aria-label'?: string
  /**
   * For a link that duplicates one already on the page — a listing's picture beside its
   * linked name. Pair it with `tabIndex={-1}`: the same destination announced twice, and
   * tabbed to twice, is noise rather than help.
   */
  'aria-hidden'?: boolean | 'true' | 'false'
  tabIndex?: number
  onClick?: () => void
  /**
   * Open in a new tab. Only meaningful with `href` — a router `Link` has nowhere to put a
   * `target`, and an in-app route opening a second tab of the same app is never wanted.
   * Ignored on the `to` and the no-destination branches.
   */
  newTab?: boolean
}

/**
 * One link component for two kinds of destination.
 *
 * KKF is four apps on four domains sharing this design system, so a nav entry, a footer
 * link or a card target is either an **in-app route** or an **absolute URL on another
 * app's host**. A router `Link` cannot carry the second — it would try to resolve
 * `https://garage.kkfmongolia.com` as a path — and a plain `<a>` throws away client-side
 * routing for the first.
 *
 * `SmartLink` picks the element from which prop it was given, so every caller passes
 * content and none of them branch:
 *
 *   <SmartLink to="/about">About</SmartLink>            → <Link to="/about">
 *   <SmartLink href={links.garage}>Garage</SmartLink>   → <a href="https://garage…">
 *
 * Pass exactly one of `to` / `href`. `href` wins if both arrive, because an absolute URL
 * is the more specific instruction; with neither, a non-navigating `<a role="button">` is
 * rendered, which is what a sub-menu parent needs.
 *
 * **Whether a cross-app link opens a new tab is the caller's choice, via `newTab`.** It
 * used to be settled here — every cross-app link opened in the same tab, on the reasoning
 * that moving between the Foundation and a venture is a hand-off rather than a detour.
 * That held for a link inside a page, and it does not hold for the nav: a visitor who
 * clicks Resort in the header is exploring, and losing the Foundation page they were
 * reading is the wrong outcome. So `HeaderNav` passes `newTab` for its `href` entries
 * (2026-09-02, at the developer's direction) and everything else still defaults to the
 * same tab. Changing the default here would change every footer link and card in four
 * apps.
 */
export function SmartLink({ to, href, children, className, onClick, newTab, tabIndex, ...aria }: SmartLinkProps) {
  if (href !== undefined) {
    return (
      <a
        href={href}
        className={className}
        onClick={onClick}
        tabIndex={tabIndex}
        // `noreferrer` alongside `noopener`: the opened tab has no business knowing which
        // KKF page sent it, and older browsers only honour the pair.
        {...(newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        {...aria}
      >
        {children}
      </a>
    )
  }
  if (to !== undefined) {
    return (
      <Link to={to} className={className} onClick={onClick} tabIndex={tabIndex} {...aria}>
        {children}
      </Link>
    )
  }
  return (
    <a role="button" className={className} onClick={onClick} tabIndex={tabIndex} {...aria}>
      {children}
    </a>
  )
}
